import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MaatraLogo from '@/components/ui/atoms/MaatraLogo';
import MNavigationButton from '@/components/ui/atoms/MNavigationButton';
import MLoader from '@/components/ui/atoms/MLoader';
import MDocumentUploadOptions from '@/components/ui/molecules/MDocumentUploadOptions';
import NBACard from './NBACard';
import styles from './Insights.module.css';
import { getMemberInfo } from '@/hooks/useMembers';
import { useFamily } from '@/hooks/useFamily';
import { useInsights } from '@/hooks/useInsights';
import { useTranslation } from 'react-i18next';
import { documentService } from '@/services/documents.service';
import familyStatsService from '@/services/familyStats.service';

const Insights: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const memberInfo = getMemberInfo();
  const { isFamilyOwner, total: familyMembersCount } = useFamily();
  const [hasDocuments, setHasDocuments] = useState(false);
  const [emergencyCardReady, setEmergencyCardReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // NBA Insights hook
  const {
    loading: nbaLoading,
    getHighPriorityItems,
    getCTAConfig,
  } = useInsights({ autoFetch: memberInfo?.isExperienced }); // Only fetch for experienced users

  useEffect(() => {
    const fetchInsightsData = async () => {
      if (!memberInfo?.id) return;
      
      try {
        // Check if member has documents
        const docsService = documentService(memberInfo.id);
        const docsResponse = await docsService.hasDocuments();
        setHasDocuments(docsResponse.hasDocuments);

        // Check emergency card status from family stats
        const memberStats = await familyStatsService.getMemberStats(memberInfo.id);
        setEmergencyCardReady(memberStats.emergencyCardReady || false);
      } catch (error) {
        console.error('Error fetching insights data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInsightsData();
  }, [memberInfo?.id]);

  const handleAddHealthDocument = () => {
    setShowUploadModal(true);
  };

  const handleNavigateToEmergencyCard = () => {
    if (emergencyCardReady) {
      navigate('/dashboard/emergency-card', { state: { backUrl: '/dashboard' } });
    } else {
      navigate('/profile/emergency-card/activate', { state: { backUrl: '/dashboard' } });
    }
  };

  const handleNavigateToAddFamilyMember = () => {
    navigate('/profile/family/add-member', { state: { backUrl: '/dashboard' } });
  };

  // Get first 2 NBA items for experienced users (already sorted by priority from API)
  const firstTwoNBAItems = memberInfo?.isExperienced ? getHighPriorityItems().slice(0, 2) : [];

  const isLoading = loading || (memberInfo?.isExperienced && nbaLoading);

  if (isLoading) return <MLoader />;

  // Hide widget entirely for experienced users with no NBA items
  if (memberInfo?.isExperienced && firstTwoNBAItems.length === 0) return null;

  return (
    <>
      <div className={`${styles.insights} ${memberInfo.isExperienced ? styles.experienced : ''}`} style={!memberInfo.isExperienced ? {width: '100%', position: 'relative'} : {}} data-insights-card>
        {/* Header with logo and title */}
        <div className={styles.header}>
          <div className={styles.logo}>
            <MaatraLogo size={27.368} fill="#FFFFFF" />
          </div>
          <h2 className={styles.title}>Maatra Recommends</h2>
        </div>
        {memberInfo.isExperienced ? (
          <>
          {/* Steps section */}
        <div className={styles.stepsSection}>
          <p className={styles.stepsText}>Based on what you've added so far, here's what we recommend next</p>
        </div>
        <div className={styles.navigationButtons}>
          {/* NBA Items using NBACard */}
          {firstTwoNBAItems.map((item) => {
            // Default to Self for now - NBA response structure will be enhanced later
            const memberType = 'Self';
            const translationKey = `nba.${item.actionType}.${memberType}`;
            
            const translations = t(translationKey, { 
              returnObjects: true,
              MemberName: '' 
            });
            
            // Type assertion for translation object
            const translationObj = translations as { 
              cta?: string; 
              description?: string; 
              title?: string;
              navPath?: string;
            };
            
            // Use navPath from translations, fallback to backend CTA config
            const handleNBAAction = () => {
              // Special handling for document upload actionTypes
              const documentUploadActionTypes = ['ADD_DOCUMENT', 'UPLOAD_RECORDS', 'EMAIL_UPLOAD', 'WHATSAPP_UPLOAD'];
              if (documentUploadActionTypes.includes(item.actionType)) {
                // Open document upload modal
                setShowUploadModal(true);
                return;
              }
              
              const navPath = translationObj.navPath;
              if (navPath) {
                // Use navigation path from translations
                navigate(navPath, { state: { backUrl: '/dashboard' } });
              } else {
                // Fallback to original NBA action handling
                const config = getCTAConfig(item);
                switch (config.actionType) {
                  case 'NAVIGATE':
                    navigate(config.url || '/', { state: { backUrl: '/dashboard' } });
                    break;
                  case 'OPEN_MODAL':
                    if (config.modalType === 'DOCUMENT_UPLOAD') {
                      setShowUploadModal(true);
                    } else {
                      console.log('Open modal:', config.modalType, 'with params:', config.params);
                    }
                    break;
                  case 'TRIGGER_FUNCTION':
                    console.log('Trigger function:', config.functionName, 'with params:', config.params);
                    break;
                  default:
                    console.warn('Unknown action type:', config.actionType);
                }
              }
            };
            
            // Special handling for ADD_DOCUMENT to use Default translation
            let title = translationObj.cta || item.ctaLabel || 'Take action';
            let description = translationObj.description || 'Complete this action to improve your health experience';
            
            if (item.actionType === 'ADD_DOCUMENT' || item.actionType === 'UPLOAD_RECORDS') {
              if (typeof translations === 'object' && translations !== null) {
                // For ADD_DOCUMENT, use only the Default translation
                const docTranslations = translations as any;
                const defaultTranslation = docTranslations.Default;
                
                title = defaultTranslation?.cta || title;
                description = defaultTranslation?.description || description;
                
                // Old random selection logic (commented out):
                // const docTypes = Object.keys(docTranslations); // ['Appointment', 'Lab', 'Insurance']
                // 
                // // Use stored selection or create a new one
                // const itemKey = `${item.actionType}-${item.priority}`;
                // let selectedDocType = selectedDocTypes[itemKey];
                // 
                // if (!selectedDocType) {
                //   selectedDocType = docTypes[Math.floor(Math.random() * docTypes.length)];
                //   setSelectedDocTypes(prev => ({ ...prev, [itemKey]: selectedDocType }));
                // }
                // 
                // const selectedTranslation = docTranslations[selectedDocType];
                // 
                // title = selectedTranslation?.cta || title;
                // description = selectedTranslation?.description || description;
              }
            }
            
            return (
              <NBACard
                key={`${item.actionType}-${item.priority}`}
                title={title}
                description={description}
                onClick={handleNBAAction}
              />
            );
          })}
          
        </div>
        </>
        ) : (
          <>
          <div className={styles.stepsSection}>
          <p className={styles.stepsText}>Here are simple steps to get started</p>
        </div>

        <div className={styles.navigationButtons}>
          <MNavigationButton
            variant="insights"
            text="Add a health document"
            leftIcon="folders"
            completed={hasDocuments}
            onClick={handleAddHealthDocument}
            style={{borderBottom: 0}}
          />
          <MNavigationButton
            variant="insights"
            text="Create emergency card"
            leftIcon="medical-cross"
            completed={emergencyCardReady}
            onClick={handleNavigateToEmergencyCard}
            style={{borderBottom: 0}}
          />
          {isFamilyOwner && (
            <MNavigationButton
              variant="insights"
              text="Add family member"
              leftIcon="user"
              completed={familyMembersCount > 1}
              onClick={handleNavigateToAddFamilyMember}
              style={{borderBottom: 0}}
            />
          )}
        </div>
        </>
        )}
      </div>
      <MDocumentUploadOptions 
        open={showUploadModal} 
        onClose={() => setShowUploadModal(false)} 
      />
    </>
  );
};

export default Insights;
